<template>
    <div class="card">
        <div class="card-header bg-primary text-white">
            <div class="card-title h5 m-0">{{ $t("signingProcedureFiling.pageTitle") }}</div>
        </div>
        <div class="card-body">
            <div class="d-flex flex-row justify-content-end">
                <vs-radio
                    v-model="attachmentsFilter"
                    vs-name="attachmentsFilterRadio"
                    vs-value="all"
                >
                    <span class="mr-1" v-cloak>{{ $t("all") }}</span>
                </vs-radio>
                <vs-radio
                    v-model="attachmentsFilter"
                    vs-name="attachmentsFilterRadio"
                    vs-value="filed"
                >
                    <span class="mr-1" v-cloak>{{ $tc("signingProcedureFiling.filed", 2) }}</span>
                </vs-radio>
                <vs-radio
                    v-model="attachmentsFilter"
                    vs-name="attachmentsFilterRadio"
                    vs-value="unfiled"
                >
                    <span class="mr-1" v-cloak>{{ $tc("signingProcedureFiling.unfiled", 2) }}</span>
                </vs-radio>
            </div>
            <div class="grid-wrapper">
                <kendo-datasource
                    ref="datasource1"
                    :transport="transportSettings"
                    :schema-model-fields="schemaModelFields"
                    :schema-model-id="'id'"
                    :schema-total="'total'"
                    :schema-data="'data'"
                    :page-size="defaultGridRows"
                    :server-paging="true"
                    :server-sorting="true"
                    :server-filtering="true"
                />
                <kendo-grid
                    ref="filingAttachmentsGrid"
                    :data-source-ref="'datasource1'"
                    :editable="gridOptions.editable"
                    :filterable="gridOptions.filterable"
                    :pageable="gridOptions.pageable"
                    :reorderable="gridOptions.reorderable"
                    :resizable="gridOptions.resizable"
                    :groupable="gridOptions.groupable"
                    :allow-copy="gridOptions.allowCopy"
                    :column-menu="gridOptions.columnMenu"
                    :sortable-mode="gridOptions.sortable.mode"
                    :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                    :sortable-show-indexes="gridOptions.sortable.showIndexes"
                    :selectable="gridOptions.selectable"
                    :scrollable="gridOptions.scrollable"
                    :dataBound="onDatabound"
                >
                    <kendo-grid-column
                        field="signingId"
                        title="Ид № на процедура"
                        :lockable="true"
                        :locked="false"
                        :hidden="true"
                    />
                    <kendo-grid-column
                        :width="400"
                        field="fileName"
                        :title="$t('signing.filesGirdHeaders.fileName')"
                        :lockable="true"
                        :locked="false"
                    />
                    <kendo-grid-column
                        :field="'description'"
                        :title="$t('signing.filesGirdHeaders.fileDescription')"
                        :lockable="true"
                        :locked="false"
                    />
                    <kendo-grid-column
                        :field="'signingCreatorShortFullName'"
                        :title="$t('signing.headers.creator')"
                        :lockable="true"
                        :locked="false"
                    />
                    <kendo-grid-column
                        :field="'signingDate'"
                        :title="$t('signing.filesGirdHeaders.timestampUtc')"
                        :format="customDateTimeFormat"
                        :lockable="true"
                        :locked="false"
                    />
                    <kendo-grid-column
                        :field="'signatoryName'"
                        :title="$t('signing.filesGirdHeaders.signedBy')"
                        :lockable="true"
                        :locked="false"
                    />
                    <kendo-grid-column
                        :width="150"
                        :field="'attachmentIsFiled'"
                        :title="$tc('signingProcedureFiling.filed',1)"
                        :template="(e) => isFiledCellTemplate(e)"
                        :lockable="true"
                        :locked="false"
                    />
                    <kendo-grid-column
                        :field="'attachmentFilingDate'"
                        :title="$t('signingProcedureFiling.headers.filingDate')"
                        :format="customDateTimeFormat"
                        :lockable="true"
                        :locked="false"
                    />
                    <kendo-grid-column
                        :field="'attachmentFiledByName'"
                        :title="$t('signingProcedureFiling.headers.filedBy')"
                        :lockable="true"
                        :locked="false"
                    />
                    <kendo-grid-column
                        :field="'attachmentFilingDetails'"
                        :title="$t('signingProcedureFiling.headers.number')"
                        :lockable="true"
                        :locked="false"
                    />
                    <kendo-grid-column
                        :width="80"
                        :template="btnsTemplate"
                        field="btnsColumn"
                        title=" "
                        :filterable="false"
                        :sortable="false"
                    />
                </kendo-grid>
            </div>
        </div>

        <DisplayModal v-model="activeItem" :show.sync="showDisplay" @onClose="onModalClose" />
    </div>
</template>

<script>
import Vue from 'vue';
import DisplayModal from '@/components/signatures/display.modal';
import BtnsTemplate from '@/components/grid/StandardBtnsTemplate';
import signingService from '@/services/signing.service';
import { mapGetters } from 'vuex';

export default {
    name: 'SigningProcedureFilingListView',
    components: {
        DisplayModal
    },
    data() {
        return {
            schemaModelFields: {
                id: { name: 'id', type: 'number', nullable: false },
                description: { name: 'description', type: 'string', nullable: true },
                fileName: { name: 'fileName', type: 'string', nullable: true },
                type: { name: 'type', type: 'string', nullable: true },
                createdBy: { name: 'createdBy', type: 'string', nullable: true },
                createdOn: { name: 'createdOn', type: 'date', nullable: true },
                docTypeCode: { name: 'docTypeCode', type: 'string', nullable: true },
                signatoryName: { name: 'signatoryName', type: 'string', nullable: true },
                signingDate: { name: 'signingDate', type: 'date', nullable: true },
                signingId: { name: 'signingId', type: 'number', nullable: false },
                signingTitle: { name: 'signingTitle', type: 'string', nullable: true },
                signingCreatorShortFullName: { name: 'signingCreatorShortFullName', type: 'string', nullable: true },
                signingCreatorFullName: { name: 'signingCreatorFullName', type: 'string', nullable: true },
                assigneeId: { name: 'assigneeId', type: 'string', nullable: true },
                assigneeShortFullName: { name: 'assigneeShortFullName', type: 'string', nullable: true },
                assigneeFullName: { name: 'assigneeFullName', type: 'string', nullable: true },
                signingDeadline: { name: 'signingDeadline', type: 'date', nullable: true },
                signingIsOverdue: { name: 'signingIsOverdue', type: 'boolean', nullable: true },
                attachmentIsSigned: { name: 'attachmentIsSigned', type: 'boolean', nullable: true },
                signingStatus: { name: 'signingStatus', type: 'string', nullable: true },
                signingStatusName: { name: 'signingStatusName', type: 'string', nullable: true },
                attachmentIsFiled: { name: 'attachmentIsFiled', type: 'boolean', nullable: true },
                attachmentFilingDate: { name: 'attachmentFilingDate', type: 'date', nullable: true },
                attachmentFiledByName: { name: 'attachmentFiledByName', type: 'string', nullable: true },
                attachmentFilingDetails: { name: 'attachmentFilingDetails', type: 'string', nullable: true },
            },
            transportSettings: {
                read: {
                    url: this.$store.getters.baseUrl + '/api/signing/FilingAttachments',
                    dataType: 'json',
                    type: 'POST',
                    contentType: 'application/json',
                    beforeSend: this.beforeSend
                },
                parameterMap: this.parameterMap
            },
            showDisplay: false,
            activeItem: null,
            attachmentsFilter: 'unfiled',
            defaultGridRows: this.$store.getters.defaultGridRows,
        };
    },
    computed: {
        ...mapGetters(['claims', 'gridOptions', 'authToken', 'dateTimeFormat']),
        customDateTimeFormat() {
            return `{0: ${this.dateTimeFormat}}`;
        },
    },
    watch: {
        attachmentsFilter: function () {
            this.refresh();
        },
    },
    methods: {
        beforeSend: function (req) {
            if (this.authToken) {
                req.setRequestHeader('Authorization', 'Bearer ' + this.authToken);
            }
        },
        onDatabound(e) {
            var items = e.sender.items();
            items.each(function (idx, item) {
                let dataItem = e.sender.dataItem(item);
                if (!dataItem.attachmentIsFiled) {
                    $(item).addClass('left-border-blue');
                }
            });
        },
        parameterMap: function (options, operation) {
            if (operation !== 'read' && options.models) {
                return { models: kendo.stringify(options.models) };
            } else {
                let newOptions = {
                    request: options,
                    attachmentsFilter: this.attachmentsFilter,
                    signingProceduresFilter: this.signingProceduresFilter
                };
                return kendo.stringify(newOptions);
            }
        },
        refresh() {
            this.$refs['filingAttachmentsGrid'].kendoWidget().dataSource.read();
        },
        btnsTemplate: function (e) {
            return {
                template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                templateArgs: {
                    ...e,
                    // parentComponent: this.$refs.createdByMeGrid,
                    btns: [
                        {
                            name: 'details',
                            text: '',
                            tooltip: this.$t('buttons.details.tooltipText'),
                            class: 'btn btn-info',
                            icon: 'fas fa-eye',
                            show: this.claims.signingProcedureFiling.read,
                            clickHandler: this.showDisplayModalFromAttachment
                        },
                        {
                            name: 'download',
                            text: '',
                            tooltip: this.$t('buttons.download.tooltipText'),
                            class: 'btn btn-success',
                            icon: 'fas fa-download',
                            show: this.claims.signingProcedureFiling.read,
                            clickHandler: this.downloadDoc
                        },
                        {
                            name: 'file',
                            text: '',
                            tooltip: this.$t('signingProcedureFiling.file'),
                            class: 'btn btn-success',
                            icon: 'fas fa-edit',
                            show: !e.attachmentIsFiled && this.claims.signingProcedureFiling.create,
                            clickHandler: this.fileAttachment
                        },
                    ]
                }
            };
        },
        async showDisplayModalFromAttachment(item) {
            const result = await signingService.getById(item.signingId);
            if (result.data) {
                this.activeItem = result.data;
                this.showDisplay = true;
            }
        },
        onModalClose() {
            this.activeItem = null;
            this.showDisplay = false;
        },
        downloadDoc(e) {
            const downloadUri = this.$store.getters.baseUrl + `/api/file/download/?id=${e.id}&type=${e.docTypeCode}&hash=${e.hash}`;
            this.loading = true;
            this.$http.get(downloadUri, { responseType: 'blob' })
                .then(response => {
                    const blob = new Blob([response.data]);
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', e.fileName); // or any other extension
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(url);
                })
                .catch(err => {
                    this.$vs.notify({
                        color: 'danger',
                        title: this.$t('notifications.error'),
                        text: this.$helpers.errorText(err)
                    })
                })
                .then(() => { this.loading = false; });
        },
        isFiledCellTemplate(dataItem) {
            let templateHtml =
                '<span class="badge ' + (dataItem.attachmentIsFiled ? 'badge-success' : 'badge-danger') + '">' + kendo.htmlEncode(dataItem.attachmentIsFiled ? this.$t('yes') : this.$t('no')) + '</span>';

            return templateHtml;
        },
        fileAttachment(item) {
            console.log(item);
            kendo.prompt(`${this.$t('signingProcedureFiling.filePrompt', { name: item.description || item.fileName })}`, '')
                .then(function (data) {
                    if (!data) {
                        return this.$vs.notify({
                            title: this.$t('signingProcedureFiling.file'),
                            text: this.$t('validation.requiredField'),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    }

                    let model = {
                        attachmentId: item.id,
                        attachmentDocTypeCode: item.docTypeCode,
                        filingDetails: data
                    };

                    signingService.fileAttachment(model)
                        .then(response => {
                            this.refresh();

                            this.$vs.notify({
                                title: this.$t('signingProcedureFiling.file'),
                                text: this.$t('notifications.successfulSavedData'),
                                color: 'success',
                                time: this.$store.getters.notificationDuration
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('signingProcedureFiling.file'),
                                text: this.$helpers.errorText(error),
                                color: 'danger',
                                time: this.$store.getters.notificationDuration
                            });
                        });
                }.bind(this));
        },
    }
};
</script>

<style lang="scss">
    tr.checkbox-disabled  input.k-checkbox {
        display: none !important;
    }

    tr.checkbox-disabled  label.k-checkbox-label {
        display: none !important;
    }

    tr.left-border-danger {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: rgba(255, 0, 0, 0.7) !important;
        }

        td:first-child {
            border-left: 7px solid rgba(255, 0, 0, 0.5) !important;
        }
    }

    tr.left-border-warning {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: rgba(255, 127, 7, 0.7) !important;
        }

        td:first-child {
            border-left: 7px solid rgba(255, 127, 7, 0.5) !important;
        }
    }

    tr.left-border-blue {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: #3498db !important;
        }

        td:first-child {
            border-left: 7px solid #3498db !important;
        }
    }

    tr.left-border-green {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: #4caf50 !important;
        }

        td:first-child {
            border-left: 7px solid #4caf50 !important;
        }
    }
</style>
